// ScrollVote.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import UserContext from "../../Contexts/User";
import AppContext from "Contexts/AppContext";
import ScrollHeader from "./ScrollHeader";
import ScrollFooter from "./ScrollFooter";
import LikeShareActions from "./LikeShareActions";
import ShareModal from "Components/shareModal";
import VoteSection from "./VoteSection";

import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { useVoteHandler } from "hooks/useVoteHandler";
import { useVideoControl } from "hooks/useVideoControl";
import {
  Container,
  Loader,
  ReelCard,
  HeroImage,
  HeroVideo,
  HeroBackdrop,
  Overlay,
  BottomGradient,
  BottomHUD,
  TitleTop,
  VoteLoaderOverlay,
  VoteLoaderText,
  DivForScroll,
  InSideDivScroll,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateSubtitle,
  ProductTag,
  ProductImageWrapper,
  ProductImage,
  ProductLinkIcon,
  ProductName,
} from "./ScrollVote.styles";
import { useNavigate } from "react-router-dom";
import { useDailyMatchups } from "hooks/useDailyMatchups";
import { DemoItem } from "common/utils/helper";
import { VoteBackgroundAudio } from "common/utils/SoundClick";
import VoteAndPower from "./VoteAndPower";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { showToast } from "App";
import { ToastType } from "Contexts/Notification";
import { createPortal } from "react-dom";
import PlayPauseButton from "./PlayPauseButton";
import { createCenterClickHandler } from "./centerClickHandler";

const SENTINEL_ID = "infinite-sentinel";

const ScrollVote: React.FC = () => {
  const BATCH_SIZE = 10;
  const { userInfo } = useContext(UserContext);
  const { login, setLogin, setSignup, setLoginRedirectMessage } = useContext(AppContext);
  const navigate = useNavigate();
  // show a transient icon per video (true when we temporarily want to show the icon)
  const [iconVisibleMap, setIconVisibleMap] = useState<Record<string, boolean>>({});
  // timers per video to auto-hide icon
  const iconTimers = useRef<Record<string, number | null>>({});


  const {
    items,
    setItems,
    dataSource,
    loadMore,
    hasMore,
    isLoading,
    setIsLoading,
    isEmpty,
  } = useDailyMatchups(userInfo, 10);


  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const playPauseDebounce = useRef<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number>(0);
  const [buttonsVisible, setButtonsVisible] = useState<Record<string, boolean>>({});
  const [likedItems, setLikedItems] = useState<Record<string, boolean>>({});
  const [shareModalShow, setShareModalShow] = useState(false);
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  const [scrollToTop, setScrollToTop] = useState(true);
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  const [playingMap, setPlayingMap] = useState<Record<string, boolean>>({});
  const [showFsPrompt, setShowFsPrompt] = useState(false);
  const {
    registerRef,
    handleVideoPlayIntent,
    clearTimersOutsideWindow,
    playPauseByIndex,
    cleanup: cleanupVideoTimers,
    videoRefs,
  } = useVideoControl();


  useInfiniteScroll(containerRef, sentinelRef, loadMore, items);


  const onScrollRAF = useRef<number | null>(null);
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    if (onScrollRAF.current) cancelAnimationFrame(onScrollRAF.current);
    onScrollRAF.current = requestAnimationFrame(() => {
      const { scrollTop, clientHeight, scrollHeight } = containerRef.current!;
      // Add hysteresis so tiny scroll jitter doesn't flip indexes
      const index = Math.floor(scrollTop / clientHeight + 0.3);
      setCurrentIndex(index);

      // Load more when:
      // 1. Not currently loading
      // 2. Has more data to load
      // 3. Either within 4 items of the end OR scrolled near bottom
      const nearEnd = items.length - index < 4;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - clientHeight * 2;


      if (!isLoading && hasMore && (nearEnd || nearBottom)) {
        loadMore();
      }

      // Immediately sync play/pause with active index to avoid brief audio from off-screen videos
      playPauseByIndex(index, items);
      clearTimersOutsideWindow(index, items);
    });
  }, [items, items.length, isLoading, hasMore, loadMore, playPauseByIndex, clearTimersOutsideWindow]);

  // hook up the scroll handler
  useEffect(() => {
    const el = containerRef.current;
    el?.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el?.removeEventListener("scroll", handleScroll);
      if (onScrollRAF.current) cancelAnimationFrame(onScrollRAF.current);
    };
  }, [handleScroll]);

  // voting handler
  const { vote, isLoadingResult: isVoteLoading } = useVoteHandler(
    userInfo,
    setItems
  );
  const actionLocks = useRef<{ [key: string]: boolean }>({});
  const ShareVoteUrl = useRef(`${document.location.protocol}//${document.location.host}`);

  const shareText = "Give a vote on these votes and win unique cards!";

  // --- NEW: muted state per video ---
  const [mutedMap, setMutedMap] = useState<Record<string, boolean>>({});

  // initialize mutedMap for loaded items (default to false)
  useEffect(() => {
    if (!items || items.length === 0) return;
    setMutedMap((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const it of items) {
        if (next[it.id] === undefined) {
          next[it.id] = false; // default unmuted
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [items]);

  // helper to get video element from videoRefs (works if videoRefs is either an object or a ref object)
  const handleCenterClick = createCenterClickHandler({
    videoRefs,
    iconTimers,
    setPlayingMap,
    setIconVisibleMap,
  });


  // side button actions (like/share/profile)
  const allSideButton = useCallback(
    async (type: string, id?: string, item?: DemoItem) => {
      if (!userInfo?.uid) {
        setLoginRedirectMessage("cast your vote");
        setLogin(true);
        return;
      }


      // Handle UI-only actions
      const uiActions: Record<string, () => void> = {
        share: () => {
          ShareVoteUrl.current = `${window.location.origin}?id=${id}`;
          setShareModalShow(true)
        },
        profile: () => navigate("/profile/mine"),
      };
      if (uiActions[type]) {
        uiActions[type]();
        return;
      }

      // Handle "like" action
      if (type !== "like" || !id || !item) return;

      // If already processing this item, ignore clicks
      if (actionLocks.current[id]) return;

      actionLocks.current[id] = true; // Lock this item

      const nextLiked = !item.isLiked;

      try {
        const likeOrUnlike = httpsCallable(functions, "likeOrUnlikeDailyMatchup");
        setItems((prev) =>
          prev.map((i) => (i.id === id ? { ...i, isLiked: nextLiked } : i))
        );
        const res = await likeOrUnlike({
          matchupId: id,
          action: nextLiked ? "like" : "unlike",
        });

        const isLiked = (res?.data as any)?.isLiked ?? nextLiked;

      } catch (err: any) {
        setItems((prev) =>
          prev.map((i) => (i.id === id ? { ...i, isLiked: item.isLiked } : i))
        );
        showToast(err.message || "Failed to update like status", ToastType.ERROR);
      } finally {
        actionLocks.current[id] = false; // Unlock after request finishes
      }
    },
    [
      userInfo?.uid,
      setLogin,
      setLoginRedirectMessage,
      navigate,
      setShareModalShow,
      functions,
      setItems,
    ]
  );


  // cleanup timers when unmount
  useEffect(() => () => cleanupVideoTimers(), [cleanupVideoTimers]);

  useEffect(() => {
    if (items.length > 0 && !isLoading && !login) {
      VoteBackgroundAudio.play();
    } else {
      VoteBackgroundAudio.pause();
    }
    return () => VoteBackgroundAudio.pause();
  }, [items, isLoading]);

  const showButtonsFor = useCallback((id: string) => setButtonsVisible((p) => ({ ...p, [id]: true })), []);




  return (
    <Container ref={containerRef}    >
      {createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, width: "100%", zIndex: 1000 }}>
          <ScrollHeader />
        </div>,
        document.body
      )}
      {items.length === 0 && isLoading && (
        <VoteLoaderOverlay>
          <VoteLoaderText className="loading">Vote Loading...</VoteLoaderText>
        </VoteLoaderOverlay>
      )}
      {isEmpty && !isLoading && (
        <EmptyStateContainer>
          <EmptyStateIcon>📊</EmptyStateIcon>
          <EmptyStateTitle>No Matchups Available</EmptyStateTitle>
          <EmptyStateSubtitle>
            There are no matchups to vote on right now. Check back soon for exciting new content!
          </EmptyStateSubtitle>
        </EmptyStateContainer>
      )}
      {/* items */}
      {isVoteLoading && (
        <VoteLoaderOverlay>
          <VoteLoaderText className="loading">Processing vote…</VoteLoaderText>
        </VoteLoaderOverlay>
      )}
      {items?.map((item: DemoItem, idx) => {
        const isActive = Math.abs(idx - currentIndex) <= 2;
        const isPlaying = !!playingMap[item.id];
        const isMuted = mutedMap[item.id] ?? false;

        return (
          <ReelCard key={idx}>
            {item.mediaType === "image" ? (
              isActive ? (
                <>
                  <HeroBackdrop src={item.mediaSrc} alt="" aria-hidden onError={(e) => ((e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/images/no_logo.png`)} />
                  <HeroImage src={item.mediaSrc} alt={item.alt || item.title} onError={(e) => ((e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/images/no_logo.png`)} loading="lazy" />
                </>
              ) : (
                <div style={{ position: "relative", zIndex: 1, height: "100%", width: "100%", background: "#000" }} />
              )
            ) : isActive ? (
              videoErrors[item.id] ? (
                <HeroImage src={item.mediaSrc} alt={item.alt || item.title} onError={(e) => ((e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/images/no_logo.png`)} />
              ) : (
                <div style={{ position: "relative", height: "100%", width: "100%" }}>
                  <HeroVideo
                    src={item.mediaSrc}
                    autoPlay={idx === currentIndex}
                    loop
                    playsInline
                    preload={idx === currentIndex ? "auto" : "metadata"}
                    ref={(el) => {
                      if (el) {
                        // ensure muted state is applied from mutedMap (default false)
                        const shouldBeMuted = mutedMap[item.id] ?? false;
                        try {
                          el.muted = shouldBeMuted;
                          if (shouldBeMuted) {
                            el.setAttribute("muted", "");
                          } else {
                            el.removeAttribute("muted");
                          }
                          el.playsInline = true;
                          el.setAttribute('playsinline', '');
                        } catch (e) {
                          // ignore
                        }
                      }
                      registerRef(item.id, el);
                    }}
                    onLoadedData={(e) => {
                      const v = e.currentTarget;
                      // Only attempt autoplay for the active card when muted; browsers block autoplay with sound.
                      if (idx === currentIndex && v.paused && v.muted) {
                        v.play().catch(() => { });
                      }
                    }}
                    onPlay={() => handleVideoPlayIntent(item.id, () => showButtonsFor(item.id))}
                    onError={() => {
                      setVideoErrors((prev) => ({ ...prev, [item.id]: true }));
                    }}
                    style={{ width: "auto", height: "100%", objectFit: "cover" }}
                  />

                  {/* --- Center click overlay (transparent) --- */}
                  {/* Center clickable hit area - always present so users can tap even when no icon */}
                  <PlayPauseButton
                    ariaLabel="Toggle play/pause"
                    onClick={() => handleCenterClick(item.id)}
                    showPauseIcon={iconVisibleMap[item.id] && !playingMap[item.id]}
                  />
                </div>
              )
            ) : (
              <div style={{ position: "relative", zIndex: 1, height: "100%", width: "100%", background: "#000" }} />
            )}

            {isActive && (
              <Overlay>
                <BottomGradient />

                {/* Product Tag in Top Right Corner - Only show after voting */}
                {item.voted && item.product_name && item.product_link && (
                  <ProductTag
                    href={item.product_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.product_image ? (
                      <ProductImageWrapper>
                        <ProductImage
                          src={item.product_image}
                          alt={item.product_name}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <ProductLinkIcon>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                          </svg>
                        </ProductLinkIcon>
                      </ProductImageWrapper>
                    ) : (
                      <ProductLinkIcon style={{ position: 'relative', top: 0, right: 0, marginBottom: '4px' }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                      </ProductLinkIcon>
                    )}
                    <ProductName>
                      {item.product_name.length > 14
                        ? `${item.product_name.substring(0, 14)}...`
                        : item.product_name}
                    </ProductName>
                  </ProductTag>
                )}

                <BottomHUD>
                  <TitleTop style={{ bottom: `${window.innerWidth > 767 ? "170px" : "140px"}`, width: `${window.innerWidth > 767 ? "55%" : "71%"}` }}>{item.title}</TitleTop>
                  {(item.mediaType === "image" || videoErrors[item.id] || buttonsVisible[item.id]) && (
                    <VoteSection
                      item={item}
                      onVote={(id, v) => vote(id, v)}
                      isLoading={isVoteLoading}
                      // voteID={isActive ? item.id : undefined}
                      voteID={items[currentIndex]?.id}
                    />
                  )}
                </BottomHUD>

                <LikeShareActions
                  liked={item?.isLiked}
                  onLike={() => allSideButton("like", item.id, item)}
                  onShare={() => allSideButton("share", item.id)}
                  onProfile={() => allSideButton("profile")}
                  userId={item?.userId || ""}
                />
              </Overlay>
            )}
          </ReelCard>
        );
      })}
      {shareModalShow && (
        <ShareModal
          shareModalShow={shareModalShow}
          setShareModalShow={setShareModalShow}
          url={ShareVoteUrl.current}
          shareText={shareText}
          hedingText="Share your vote → earn more. Friends who join from your link help you mine faster and if they buy in-app, you get a cut."
          fullUrl={ShareVoteUrl.current}
        />
      )}
      <div id={SENTINEL_ID} ref={sentinelRef} />
      {createPortal(
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, width: "100%", zIndex: 1000 }}>
          <ScrollFooter />
        </div>,
        document.body
      )}
    </Container>
  );
};

export default ScrollVote;
